/**
 * Shared scaffold for Environnement block cards.
 *
 * Every env card follows the same visual rhythm :
 *   - Header row with icon chip + title + loading/fresh/cache status
 *   - Body that swaps between idle/loading skeleton, error notice, and the
 *     caller-provided "ready" slot.
 *
 * Keeping the shell factored out means a new block (DPE, transports, ARCEP)
 * is 10 lines of boilerplate plus the actual data rendering — the status
 * row, skeleton, and error fallback all live here.
 */

import type { ReactNode } from "react";
import { Card } from "../ui";

type State =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "ready"; fromCache: boolean; fetchedAt: Date | null }
  | { status: "error"; message: string };

type Props = {
  icon: ReactNode;
  title: string;
  // Shown under the title when status is "ready" (e.g. "12 écoles · 1,5 km").
  subtitle?: ReactNode;
  // Tiny status pill shown at the far right of the header — we roll our own
  // here rather than using <Pill> because it needs a more subdued tone so
  // it doesn't compete with data-level pills.
  state: State;
  children: ReactNode;
  // Optional footer (e.g. "Source : data.education.gouv.fr").
  footer?: ReactNode;
  // Toggle-style slot on the right of the header — used for the "Afficher
  // sur carte" button on blocks that support a map view.
  headerRight?: ReactNode;
};

export function EnvCardShell({
  icon,
  title,
  subtitle,
  state,
  children,
  footer,
  headerRight,
}: Props) {
  return (
    <Card className="p-5">
      <header className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-xl bg-ink-50 hairline grid place-items-center shrink-0 text-ink-700">
          {icon}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="text-[15px] font-semibold tracking-tight text-ink-900 truncate">
              {title}
            </h3>
            <StatusChip state={state} />
          </div>
          {subtitle && (
            <div className="text-[12.5px] text-ink-600 mt-0.5 truncate">
              {subtitle}
            </div>
          )}
        </div>
        {headerRight && <div className="shrink-0">{headerRight}</div>}
      </header>

      <div className="mt-4">
        {state.status === "idle" || state.status === "loading" ? (
          <Skeleton />
        ) : state.status === "error" ? (
          <ErrorBox message={state.message} />
        ) : (
          children
        )}
      </div>

      {footer && (
        <div className="mt-4 pt-3 border-t border-ink-100 text-[11px] text-ink-500">
          {footer}
        </div>
      )}
    </Card>
  );
}

// ─── Status chip ────────────────────────────────────────────────────────

function StatusChip({ state }: { state: State }) {
  if (state.status === "loading" || state.status === "idle") {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] text-ink-500">
        <span className="w-1.5 h-1.5 rounded-full bg-ink-300 animate-pulse" />
        chargement
      </span>
    );
  }
  if (state.status === "error") {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] text-rose-700">
        <span className="w-1.5 h-1.5 rounded-full bg-rose-500" />
        indisponible
      </span>
    );
  }
  // ready
  const label = state.fromCache ? "en cache" : "à jour";
  return (
    <span className="inline-flex items-center gap-1 text-[11px] text-ink-500">
      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
      {label}
    </span>
  );
}

// ─── Skeleton ───────────────────────────────────────────────────────────

function Skeleton() {
  return (
    <div className="space-y-2 animate-pulse">
      <div className="h-3 rounded bg-ink-100 w-3/4" />
      <div className="h-3 rounded bg-ink-100 w-1/2" />
      <div className="h-3 rounded bg-ink-100 w-2/3" />
      <div className="h-3 rounded bg-ink-100 w-2/5" />
    </div>
  );
}

// ─── Error fallback ─────────────────────────────────────────────────────

function ErrorBox({ message }: { message: string }) {
  // We keep the actual error text for dev inspection but never surface it
  // raw — the user only needs to know we couldn't reach the source.
  return (
    <div className="rounded-lg bg-rose-50 border border-rose-100 px-3 py-2.5">
      <div className="text-[12.5px] text-rose-800">
        Source temporairement indisponible. Réessayez dans quelques minutes.
      </div>
      <div className="text-[11px] text-rose-600 mt-1 font-mono truncate">
        {message}
      </div>
    </div>
  );
}
